import React, { useEffect, useState, useRef } from 'react';
import { connect } from 'react-redux';
import { Grid, MenuItem, TextField, Typography } from '@mui/material';
import MainCard from 'ui-component/cards/MainCard';
import LoaderCircular from 'ui-component/LoaderCircular';
import Chart from 'react-apexcharts';
import { isWidgetAllowed } from '../../utils/widgetPermissions';
import '../Dashboard/styleSearch.scss';
import { gridSpacing } from 'store/constant';

const metricsLabels = [
  { value: 'clicks', label: 'Clicks' },
  { value: 'impressions', label: 'Impressions' },
  { value: 'installs', label: 'MMP Installs' },
  { value: 'mmp_events', label: 'MMP Events' },
  { value: 'skan_installs', label: 'SKAN Installs' },
  { value: 'skan_events', label: 'SKAN Events' }
];

function formatNumberAbbreviated(value) {
  if (value === 0) return '0';
  if (!value || Number.isNaN(value)) return '0';

  const suffixes = ['', 'K', 'M', 'B', 'T'];
  if (value < 1000) return value.toString();

  const exp = Math.floor(Math.log10(value) / 3);
  const shortValue = (value / Math.pow(1000, exp)).toFixed(1);
  return `${shortValue}${suffixes[exp]}`;
}

const WowSkanView = ({
  wowReportData,
  loading,
  error,
  timeZone,
  isDollar,
  userDashboardPermissions,
  isSkan,
  advertiser_campaign_fetching
}) => {
  const [options, setOptions] = useState();
  const [allSeries, setAllSeries] = useState([]);
  const [selectedMetric, setSelectedMetric] = useState('clicks'); // Default to "Clicks"
  const [selectedCampaigns, setSelectedCampaigns] = useState(new Set());
  const [metricsLabelsNeeded, setMetricsLabelsNeeded] = useState([]);
  const chartRef = useRef(null);

  // Combined loading state
  const isLoading = loading || advertiser_campaign_fetching;

  // Zoom prevention effect remains the same
  useEffect(() => {
    const chartElement = chartRef.current;
    if (chartElement) {
      const preventZoom = (e) => {
        if (e.ctrlKey || e.metaKey) {
          e.preventDefault();
        }
      };

      const preventWheelZoom = (e) => {
        if (e.ctrlKey) {
          e.preventDefault();
        }
      };

      document.addEventListener('wheel', preventWheelZoom, { passive: false });
      document.addEventListener('keydown', preventZoom);

      return () => {
        document.removeEventListener('wheel', preventWheelZoom);
        document.removeEventListener('keydown', preventZoom);
      };
    }
  }, []);

  // Filter metrics based on user's widget permissions
  useEffect(() => {
    // Filter metrics based on user's widget permissions
    const dataType = isSkan ? 'skan' : 'nonskan';
    const filteredMetrics = metricsLabels.filter((metric) => {
      return isWidgetAllowed(metric.value, userDashboardPermissions, timeZone, dataType);
    });

    setMetricsLabelsNeeded(filteredMetrics);

    // Ensure selected metric is still valid after filtering
    if (filteredMetrics.length > 0) {
      const isCurrentMetricValid = filteredMetrics.some((metric) => metric.value === selectedMetric);
      if (!isCurrentMetricValid) {
        // If current metric is not available, default to "clicks" or first available metric
        const defaultMetric = filteredMetrics.find((metric) => metric.value === 'clicks') || filteredMetrics[0];
        setSelectedMetric(defaultMetric.value);
      }
    }
  }, [timeZone, userDashboardPermissions, isSkan]);

  // Main chart data processing effect
  useEffect(() => {
    if (!wowReportData || wowReportData.length === 0) return;

    // Initialize selected campaigns if none
    let campaignsToUse = selectedCampaigns;
    if (selectedCampaigns.size === 0) {
      campaignsToUse = new Set(wowReportData.map((item) => item.campaign_name));
      setSelectedCampaigns(campaignsToUse);
    }

    // Sort by year/week
    const sortedData = [...wowReportData].sort((a, b) => {
      if (a.year === b.year) return parseInt(a.week_number) - parseInt(b.week_number);
      return a.year - b.year;
    });

    // Group by week-year
    const groupedData = sortedData.reduce((acc, item) => {
      const weekYear = `Week ${item.week_number}-${item.year}`;
      if (!acc[weekYear]) acc[weekYear] = { metricData: {}, media_cost: 0 };

      if (campaignsToUse.has(item.campaign_name)) {
        if (!acc[weekYear].metricData[item.campaign_name]) {
          acc[weekYear].metricData[item.campaign_name] = 0;
        }
        acc[weekYear].metricData[item.campaign_name] += item[selectedMetric] || 0;
        acc[weekYear].media_cost += isDollar ? item.media_cost || 0 : item.media_cost_local_currency || 0;
      }
      return acc;
    }, {});

    const weeks = Object.keys(groupedData);

    // Metric (bars) series → always yAxisIndex 0 (left)
    // const metricSeries = Object.keys(groupedData[weeks[0]]?.metricData || {}).map((campaignName) => ({
    //   name: campaignName,
    //   type: 'column',
    //   yAxisIndex: 0,
    //   data: weeks.map((w) => groupedData[w]?.metricData[campaignName] || 0)
    // }));

    // // Media cost (line) series → always yAxisIndex 1 (right)
    // const mediaCostSeries = {
    //   name: 'Media Cost',
    //   type: 'line',
    //   yAxisIndex: 1,
    //   data: weeks.map((w) => groupedData[w]?.media_cost || 0)
    // };

    // Axis scaling
    // const metricMax = metricSeries.length > 0 ? Math.max(...metricSeries.flatMap((s) => s.data.map((d) => Number(d) || 0))) : 0;
    // const mediaCostMax = mediaCostSeries.data.length > 0 ? Math.max(...mediaCostSeries.data.map((d) => Number(d) || 0)) : 0;

    // const showMediaAxis = mediaCostMax > 0; // hide axis if all zeros

    // Combine series
    // const allSeriesData = [...metricSeries, mediaCostSeries];

    // // Dynamic styling arrays
    // const strokeWidths = allSeriesData.map((s) => (s.type === 'line' ? 4 : 0));
    // const markerSizes = allSeriesData.map((s) => (s.type === 'line' ? 4 : 0));

    // // --- 🔥 Dynamic colors ---
    // const colorPalette = [
    //   '#FF5733',
    //   '#33FF57',
    //   '#3357FF',
    //   '#FF33A1',
    //   '#FFD700',
    //   '#00CED1',
    //   '#8A2BE2',
    //   '#DC143C',
    //   '#FF8C00',
    //   '#20B2AA',
    //   '#FF4500',
    //   '#6495ED',
    //   '#9ACD32'
    // ];

    // const campaignColors = metricSeries.map((_, idx) => colorPalette[idx % colorPalette.length]);

    // const allColors = [...campaignColors, '#1b1bfe']; // fixed color for Media Cost line

    // const optionsDetails = {
    //   chart: {
    //     height: 350,
    //     type: 'line',
    //     stacked: false,
    //     toolbar: {
    //       show: true,
    //       tools: {
    //         download: true,
    //         selection: false,
    //         zoom: false,
    //         zoomin: false,
    //         zoomout: false,
    //         pan: false,
    //         reset: false
    //       }
    //     },
    //     zoom: { enabled: false }
    //   },
    //   stroke: { width: strokeWidths, curve: 'smooth' },
    //   fill: { opacity: [1, ...Array(metricSeries.length).fill(0.85)] },
    //   dataLabels: { enabled: false },
    //   labels: weeks,
    //   markers: { size: markerSizes },
    //   xaxis: { type: 'category', title: { text: 'Week - Year' } },
    //   yaxis: [
    //     {
    //       // Left axis - metrics
    //       title: {
    //         text: selectedMetric.charAt(0).toUpperCase() + selectedMetric.slice(1)
    //       },
    //       min: 0,
    //       max: undefined,
    //       tickAmount: metricMax > 0 ? 5 : undefined,
    //       forceNiceScale: true,
    //       axisBorder: { show: true, color: '#FF5733' }, // bars color
    //       labels: { formatter: (value) => formatNumberAbbreviated(value) }
    //     },
    //     {
    //       // Right axis - media cost
    //       opposite: true,
    //       title: { text: 'Media Cost' },
    //       min: 0,
    //       max: undefined,
    //       tickAmount: mediaCostMax > 0 ? 5 : undefined,
    //       forceNiceScale: true,
    //       show: showMediaAxis,
    //       labels: {
    //         show: showMediaAxis,
    //         formatter: (value) => formatNumberAbbreviated(value)
    //       },
    //       axisBorder: { show: showMediaAxis, color: '#1b1bfe' } // line color
    //     }
    //   ],
    //   plotOptions: { bar: { columnWidth: '70%', distributed: false } },
    //   tooltip: {
    //     shared: true,
    //     intersect: false,
    //     y: {
    //       formatter: (value, { seriesIndex, w }) =>
    //         `${formatNumberAbbreviated(value)} ${w.config.series[seriesIndex].type === 'line' ? '(Media Cost)' : `(${selectedMetric})`}`
    //     }
    //   },
    //   legend: { horizontalAlign: 'left', offsetX: 40 },
    //   colors: allColors
    // };
    // --- Build dynamic Y-axes for each campaign ---
    const campaignNames = Object.keys(groupedData[weeks[0]]?.metricData || {});

    // Series for each campaign → each gets unique yAxisIndex
    const metricSeries = campaignNames.map((campaignName, i) => ({
      name: campaignName,
      type: 'column',
      yAxisIndex: i, // unique per campaign
      data: weeks.map((w) => groupedData[w]?.metricData[campaignName] || 0)
    }));

    // Media cost line (always right side)
    const mediaCostSeries = {
      name: 'Media Cost',
      type: 'line',
      yAxisIndex: campaignNames.length, // last index
      data: weeks.map((w) => groupedData[w]?.media_cost || 0)
    };

    // Combine series
    const allSeriesData = [...metricSeries, mediaCostSeries];

    // --- Colors ---
    const colorPalette = [
      '#FF5733',
      '#33FF57',
      '#56a6c2ff',
      '#FF33A1',
      '#FFD700',
      '#00CED1',
      '#8A2BE2',
      '#DC143C',
      '#FF8C00',
      '#20B2AA',
      '#FF4500',
      '#6495ED',
      '#9ACD32'
    ];
    const campaignColors = metricSeries.map((_, i) => colorPalette[i % colorPalette.length]);
    const allColors = [...campaignColors, '#1b1bfe']; // last color reserved for media cost line

    // --- Dynamic Y-Axes ---
    const yAxes = campaignNames.map((campaignName, i) => ({
      opposite: i % 2 === 1, // alternate sides
      title: { text: campaignName },
      labels: { formatter: (v) => formatNumberAbbreviated(v) },
      axisBorder: { show: true, color: campaignColors[i] },
      tickAmount: 5,
      forceNiceScale: true,
      min: 0
    }));

    // Add Media Cost axis (always right)
    yAxes.push({
      opposite: true,
      title: { text: 'Media Cost' },
      labels: { formatter: (v) => formatNumberAbbreviated(v) },
      axisBorder: { show: true, color: '#1b1bfe' },
      tickAmount: 5,
      forceNiceScale: true,
      min: 0
    });

    // --- Styling helpers ---
    const strokeWidths = allSeriesData.map((s) => (s.type === 'line' ? 4 : 0));
    const markerSizes = allSeriesData.map((s) => (s.type === 'line' ? 4 : 0));

    // --- Final Chart Options ---
    const optionsDetails = {
      chart: {
        height: 350,
        type: 'line',
        stacked: false,
        toolbar: {
          show: true,
          tools: {
            download: true,
            selection: false,
            zoom: false,
            zoomin: false,
            zoomout: false,
            pan: false,
            reset: false
          }
        },
        zoom: { enabled: false }
      },
      stroke: { width: strokeWidths, curve: 'smooth' },
      fill: { opacity: [1, ...Array(metricSeries.length).fill(0.85)] },
      dataLabels: { enabled: false },
      labels: weeks,
      markers: { size: markerSizes },
      xaxis: { type: 'category', title: { text: 'Week - Year' } },
      yaxis: yAxes, // dynamically built axes
      plotOptions: { bar: { columnWidth: '70%', distributed: false } },
      tooltip: {
        shared: true,
        intersect: false,
        y: {
          formatter: (value, { seriesIndex, w }) =>
            `${formatNumberAbbreviated(value)} ${w.config.series[seriesIndex].type === 'line' ? '(Media Cost)' : `(${selectedMetric})`}`
        }
      },
      legend: { horizontalAlign: 'left', offsetX: 40 },
      colors: allColors
    };

    setOptions(optionsDetails);
    setAllSeries(allSeriesData);
  }, [wowReportData, selectedMetric, isDollar, selectedCampaigns]);

  return (
    <MainCard>
      <Grid container spacing={gridSpacing}>
        <Grid item lg={12} md={12} sm={12} xs={12} alignContent="center" sx={{ textAlign: 'center' }}>
          <Typography variant="h4">Week-On-Week Overview</Typography>
        </Grid>

        {isLoading ? (
          <div style={{ height: '200px', width: '100%' }}>
            <LoaderCircular show={true} />
          </div>
        ) : allSeries.length === 0 || wowReportData.length === 0 ? (
          <div className="performanceDataContainer" ref={chartRef}>
            <Typography style={{ textAlign: 'center' }}>
              {error.length !== 0 ? (
                'No Data Found'
              ) : (
                <span>
                  Please click <b>Apply Filter</b> to view data
                </span>
              )}
            </Typography>
          </div>
        ) : (
          <>
            <Grid item lg={12} md={12} sm={12} xs={12} alignContent="center" sx={{ textAlign: 'center' }}>
              <Grid container direction="row" spacing={1} justifyContent="space-between" alignItems="center">
                <Grid item>
                  <Typography variant="h3">Metrics</Typography>
                </Grid>
                <Grid item>
                  <TextField
                    id="standard-select-currency"
                    select
                    value={selectedMetric}
                    onChange={(e) => setSelectedMetric(e.target.value)}
                  >
                    {metricsLabelsNeeded.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <div className="performanceMainContainer" ref={chartRef}>
                <Chart options={options} series={allSeries} type="line" width="100%" />
              </div>
            </Grid>
          </>
        )}
      </Grid>
    </MainCard>
  );
};

const mapStateToProps = (state) => ({
  wowReportData: state.wowReducer.wowReportData,
  loading: state.wowReducer.loading,
  error: state.wowReducer.error,
  timeZone: state.advertiserReducer.timeZone,
  isDollar: state.campaignReducer.isDollar,
  userDashboardPermissions: state.userReducer.userDashboardPermissions,
  isSkan: state.campaignReducer.isSkan,
  advertiser_campaign_fetching: state.advertiserReducer.advertiser_campaign_fetching
});

export default connect(mapStateToProps, null)(WowSkanView);
